import { SortDescriptor } from '@progress/kendo-data-query/dist/npm/sort-descriptor';
import {
  Grid,
  GridColumn,
  GridColumnMenuCheckboxFilter,
  GridColumnMenuProps,
  GridColumnProps,
  GridColumnResizeEvent,
  GridCustomFilterCellProps,
  GridFilterChangeEvent,
  GridFilterOperators,
  GridProps
} from '@progress/kendo-react-grid';
import { SettingValueType } from '@Shared.Angular/@types/core/contracts/constants/settingValueTypes';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import React, {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  useEffect,
  useRef,
  useState
} from 'react';

interface IKendoReactGridProps extends GridProps {
  id: string;
  manuallyFilteredFieldNames?: string[];
}

const SIDEBAR_WIDTH = 60;
const GRID_BUFFER = 25;
const MIN_COL_WIDTH = 50;
const DEBOUNCE_RESIZE = 500;
const DEBOUNCE_SAVE = 200;

interface IColumnInfo {
  field: string;
  width: number;
  isFixed: boolean;
}

// Pure function: Calculate total width of fixed columns
const calculateFixedColumnsWidth = (
  children: React.ReactNode
): number => {
  let fixedWidth = 0;
  Children.forEach(children, (child: ReactElement<GridColumnProps>) => {
    if (!isValidElement(child)) return;
    const isFixed = child.props.resizable === false;
    const width = Number(child.props.width);
    if (isFixed && width) {
      fixedWidth += width;
    }
  });
  return fixedWidth;
};

// Pure function: Parse stored width value
const parseStoredWidth = (
  savedValue: string | number | undefined,
  defaultWidth: number | undefined,
  flexibleSpace: number
): number | undefined => {
  if (!savedValue) return defaultWidth;
  const storedString = String(savedValue);
  if (!storedString.includes('%')) return defaultWidth;
  const percentage = parseFloat(storedString.replace('%', ''));
  return !isNaN(percentage) && flexibleSpace > 0
    ? (percentage / 100) * flexibleSpace
    : defaultWidth;
};

// Pure function: Distribute remaining space to columns without defined widths
const distributeRemainingSpace = (
  columns: IColumnInfo[],
  columnsWithoutWidth: string[],
  currentGridWidth: number
): IColumnInfo[] => {
  if (columnsWithoutWidth.length === 0) return columns;

  const usedWidth = columns.reduce((sum, column) => sum + column.width, 0);
  const widthPerColumn = Math.max(
    MIN_COL_WIDTH,
    (currentGridWidth - usedWidth - GRID_BUFFER) / columnsWithoutWidth.length
  );

  const newColumns = columnsWithoutWidth.map((field) => ({
    field,
    width: widthPerColumn,
    isFixed: false
  }));

  return [...columns, ...newColumns];
};

// Pure function: Adjust flexible columns for overflow/underflow
const adjustFlexibleColumns = (
  columns: IColumnInfo[],
  currentGridWidth: number,
  hasColumnsWithoutWidth: boolean
): IColumnInfo[] => {
  const totalWidth = columns.reduce((sum, column) => sum + column.width, 0);
  const overflow = currentGridWidth - totalWidth - GRID_BUFFER;

  if (overflow === 0) return columns;

  const flexibleColumns = columns.filter((column) => !column.isFixed);
  const flexibleTotal = flexibleColumns.reduce(
    (sum, column) => sum + column.width,
    0
  );

  if (
    flexibleTotal > 0 &&
    (overflow < 0 || !hasColumnsWithoutWidth)
  ) {
    const adjustedColumns = columns.map((column) => {
      if (column.isFixed) return column;
      return {
        ...column,
        width: column.width + overflow * (column.width / flexibleTotal)
      };
    });
    return adjustedColumns;
  }

  return columns;
};

/**
 * A wrapper around KendoReact Grid for client-side operations (paging, sorting, filtering).
 * - Columns with resizable={false} MUST have a width prop set
 * - Column widths are saved as percentages and applied responsively
 */
export const KendoReactGrid = (props: IKendoReactGridProps) => {
  const APP_CONFIG = getService('APP_CONFIG');
  const accountService = getService('accountApiService');
  const [skip, setSkip] = useState(0);
  const [gridWidth, setGridWidth] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filterOperators = {
    text: [
      { text: 'grid.filterContainsOperator', operator: 'contains' },
      { text: 'grid.filterNotContainsOperator', operator: 'doesnotcontain' },
      { text: 'grid.filterEqOperator', operator: 'eq' },
      { text: 'grid.filterNotEqOperator', operator: 'neq' },
      { text: 'grid.filterIsEmptyOperator', operator: 'isempty' },
      { text: 'grid.filterIsNotEmptyOperator', operator: 'isnotempty' }
    ],
    date: [
      { text: 'grid.filterAfterOperator', operator: 'gt' },
      { text: 'grid.filterBeforeOperator', operator: 'lt' }
    ]
  };

  const getGridWidth = (): number =>
    gridRef.current?.offsetWidth || window.innerWidth - SIDEBAR_WIDTH;

  const getGridConfigs = () => {
    try {
      return JSON.parse(APP_CONFIG.userGridConfigs);
    } catch {
      return {};
    }
  };

  const getSavedWidths = () => getGridConfigs()[props.id]?.widths || {};

  const persistGridConfig = (field: string, widthPercentage: string) => {
    const gridConfigs = getGridConfigs();
    gridConfigs[props.id] = {
      ...gridConfigs[props.id],
      widths: {
        ...gridConfigs[props.id]?.widths,
        [field]: widthPercentage
      }
    };

    accountService.changeUserSetting(
      'UserGridConfigs',
      JSON.stringify(gridConfigs),
      SettingValueType.string,
      true
    );
  };

  const calculateWidthPercentage = (newWidth: number): string => {
    const currentGridWidth = getGridWidth();
    const fixedWidth = calculateFixedColumnsWidth(props.children);
    const flexibleSpace = currentGridWidth - fixedWidth;
    return ((newWidth / flexibleSpace) * 100).toFixed(2);
  };

  // Update grid width on mount and window resize
  useEffect(() => {
    const updateWidth = () => setGridWidth(getGridWidth());
    updateWidth();

    let timeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(updateWidth, DEBOUNCE_RESIZE);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  const saveColumnWidths = (event: GridColumnResizeEvent) => {
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      const column = event.columns[event.index];
      if (!column.field) return;

      const percentage = calculateWidthPercentage(event.newWidth);
      persistGridConfig(column.field, `${percentage}%`);
    }, DEBOUNCE_SAVE);
  };

  const buildColumnWidths = (): Map<string, number> => {
    const savedWidths = getSavedWidths();
    const currentGridWidth = gridWidth || getGridWidth();
    const fixedWidth = calculateFixedColumnsWidth(props.children);
    const flexibleSpace = currentGridWidth - fixedWidth;

    let columns: IColumnInfo[] = [];
    const columnsWithoutWidth: string[] = [];
    let columnIndex = 0;

    // Parse all columns and categorize them
    Children.forEach(props.children, (child: ReactElement<GridColumnProps>) => {
      if (!isValidElement(child)) return;

      const field = child.props.field || `__column_${columnIndex++}__`;
      const defaultWidth = Number(child.props.width);
      const isFixed = child.props.resizable === false;

      if (isFixed && defaultWidth) {
        columns.push({ field, width: defaultWidth, isFixed: true });
      } else {
        const width = parseStoredWidth(
          savedWidths[field],
          defaultWidth,
          flexibleSpace
        );
        width
          ? columns.push({ field, width, isFixed: false })
          : columnsWithoutWidth.push(field);
      }
    });

    // Apply width distribution and adjustments
    columns = distributeRemainingSpace(
      columns,
      columnsWithoutWidth,
      currentGridWidth
    );
    columns = adjustFlexibleColumns(
      columns,
      currentGridWidth,
      columnsWithoutWidth.length > 0
    );

    return new Map(
      columns.map((column) => [
        column.field,
        Math.max(column.width, MIN_COL_WIDTH)
      ])
    );
  };

  const columnWidths = buildColumnWidths();

  const modifiedChildren = Children.map(
    props.children,
    (child: ReactElement<GridColumnProps>, index) => {
      if (!isValidElement(child)) return child;
      const field = child.props.field || `__column_${index}__`;
      const width = columnWidths.get(field);
      return width ? cloneElement(child, { ...child.props, width }) : child;
    }
  );

  return (
    <div className="kendo-react-grid" ref={gridRef}>
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <Grid
        key={`grid-${props.id}-${gridWidth}`} // Force re-render on resize
        {...props}
        filterOperators={filterOperators}
        onFilterChange={(x) => {
          setSkip(0);
          if (props.manuallyFilteredFieldNames?.length && x.filter?.filters) {
            x.filter.filters = x.filter.filters.filter(
              (f) =>
                !props.manuallyFilteredFieldNames.some(
                  (mff) => mff === (f as any).field
                )
            );
          }
          props.onFilterChange?.(x);
        }}
        onPageChange={(e) => setSkip(e.page.skip)}
        skip={skip}
        onColumnResize={saveColumnWidths}
      >
        {modifiedChildren}
      </Grid>
    </div>
  );
};

export {
  GridColumn,
  GridColumnMenuProps,
  GridColumnMenuCheckboxFilter,
  GridCustomFilterCellProps,
  GridFilterChangeEvent,
  GridFilterOperators,
  SortDescriptor
};
